/**
 * 자막 스타일별 PNG 레이아웃 순수 함수 (BOOTH-85).
 * DOM/캔버스에 의존하지 않아 vitest 로 직접 테스트한다.
 * `renderCaptionPng` 가 measure 를 주입해 이 함수를 호출하고 결과를 캔버스에 그린다.
 */

import {
  CAPTION_PNG_H,
  CAPTION_PNG_W,
  CAPTION_TEXT_Y_RATIO,
  VIDEO_PAPER_COLOR,
  captionLength,
  captionStyleFont,
  type CaptionStyle,
} from "@fiket/booth-schema";
import type { Language } from "../../i18n/locale";
// captionCanvas.ts 에서 분리된 모듈 — captionCanvas ↔ captionStyleLayout 순환 의존 방지
import { CAPTION_FONT_PX_MIN, captionFontPx } from "./captionFontSize";

/** 최소 렌더 글자 크기 — CAPTION_FONT_PX_MIN × 2 × 0.8 (PNG 는 배율 2배) */
export const CAPTION_FIT_MIN_PX = Math.round(CAPTION_FONT_PX_MIN * 2 * 0.8); // 90

/** postit 메모지 세로 패딩 비율 (fontPx 대비) */
export const NOTE_V_PAD_RATIO = 0.7;
/** postit 메모지 모서리 반경(px) — 종이답게 거의 직각 */
export const NOTE_RADIUS = 4;
/** postit 메모지(+그림자)가 PNG 아래로 삐져나오지 않도록 남기는 여백(px) */
export const NOTE_BOTTOM_MARGIN_PX = 20;
/** postit 종이 그림자 */
export const NOTE_SHADOW = { color: "rgba(0,0,0,0.25)", blur: 18, offsetY: 8 };
/** 종이 결(speckle) 하나당 담당 면적(px²) — 넓은 메모지일수록 점이 많아진다 */
export const NOTE_GRAIN_PX2_PER_SPECKLE = 60;
/** 종이 결 난수 seed — 같은 자막은 늘 같은 결(결정적 렌더) */
export const NOTE_GRAIN_SEED = 0x85;
/** 종이 결 색 (옅은 갈색) */
export const NOTE_GRAIN_COLOR = "#8A6F2E";
/** postit 마스킹 테이프 — 메모지 위쪽 가운데에 비스듬히 */
export const NOTE_TAPE = { wEm: 1.6, hEm: 0.45, rotateDeg: 6, color: "rgba(255,255,255,0.55)" };

/** 박스(tag·pill·note)가 PNG 가장자리에서 최소로 띄우는 여백(px) */
export const BOX_EDGE_MARGIN_PX = 8;

/** gilt ✦ 장식 크기(em) */
export const ORNAMENT_EM = 0.55;
/** gilt ✦ 장식과 글자 사이 간격(em) */
export const ORNAMENT_GAP_EM = 0.6;
/** brush 낙관 한 변(em) */
export const SEAL_EM = 0.45;
/** brush 낙관과 글자 사이 간격(em) */
export const SEAL_GAP_EM = 0.3;

/** 캔버스 measureText(text).width 를 흉내 내는 함수 타입 */
export type MeasureText = (font: string, text: string) => number;

/** captionStyleLayout 의 반환 타입 */
export interface CaptionLayout {
  /** captionStyleFont(style, language, fontPx) */
  font: string;
  fontPx: number;
  /** 글자 기준점(PNG px). y = CAPTION_PNG_H × CAPTION_TEXT_Y_RATIO */
  x: number;
  y: number;
  align: "center" | "left" | "right";
  rotateDeg: number;
  /** `style.letterSpacingEm` × fontPx (없으면 0) */
  letterSpacingPx: number;
  color: string;
  /** background === "shade" */
  shade: boolean;
  /**
   * 글자가 실제로 차지하는 가로 범위 — `measure + letterSpacingPx × (graphemes−1)`.
   * 박스·장식선·그라데이션이 모두 이 값을 기준으로 놓인다(공백 텍스트도 계산한다).
   */
  textLeft: number;
  textAdvance: number;
  /**
   * tag(radius 6) · pill(radius h/2) · note(radius NOTE_RADIUS).
   * 가장자리에 닿으면 `BOX_EDGE_MARGIN_PX`(+회전 lift) 만큼 패딩을 줄여 들이므로 `w` 가 `textAdvance + 2hPad` 보다 작을 수 있다.
   */
  box?: { x: number; y: number; w: number; h: number; radius: number; color: string };
  /** poster: 글자 위아래 0.18×fontPx 간격, 폭 = 글자 폭 + 2×fontPx */
  rules?: { x1: number; x2: number; y1: number; y2: number; color: string };
  /** minimal: 글자 위 0.45×fontPx, 폭 = 글자 폭 */
  hairline?: { x1: number; x2: number; y: number; color: string };
  /**
   * 글자 채움 그라데이션 끝점(회전 전 로컬 좌표) — 캔버스가 `createLinearGradient(x0,y0,x1,y1)` 에 그대로 쓴다.
   * PNG 폭이 아니라 **측정한 글자 advance/높이** 를 덮는다.
   */
  fill?: { colors: string[]; x0: number; y0: number; x1: number; y1: number };
  /** gilt: 글자 양옆 ✦. `font` 는 `${px}px ${stack}`, textAlign center·baseline middle 로 그린다 */
  ornaments?: {
    glyph: "✦";
    font: string;
    px: number;
    color: string;
    left: { x: number; y: number };
    right: { x: number; y: number };
  };
  /** brush: 글자 끝 주홍 낙관(정사각, 안쪽 종이색 테두리) */
  seal?: {
    x: number;
    y: number;
    size: number;
    radius: number;
    color: string;
    borderColor: string;
    borderWidth: number;
  };
  /** postit: box(기하) 위에 얹는 종이 그림자·결·마스킹 테이프 */
  note?: {
    shadow: { color: string; blur: number; offsetY: number };
    grain: { seed: number; count: number; color: string };
    tape: { cx: number; cy: number; w: number; h: number; rotateDeg: number; color: string };
  };
  effect: "shadow" | "glow" | "none";
  glowColor?: string;
}

/** left/right 정렬의 PNG 폭 대비 여백 비율 (6%) */
const SIDE_MARGIN_RATIO = 0.06;
/** 가운데 정렬의 좌우 여백(px) */
const CENTER_MARGIN_PX = 80;
/** tag·pill 의 세로 패딩 = 0.5 × fontPx */
const V_PAD_RATIO = 0.5;
/** tag·pill·note 의 가로 패딩 = 0.6em */
const H_PAD_EM = 0.6;

/** mulberry32 — 32비트 시드 PRNG. 같은 seed 면 늘 같은 수열(결정적 렌더용) */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * postit 메모지의 종이 결(speckle) 좌표를 결정적으로 만든다.
 * 점은 전부 box 안쪽(끝에서 size 만큼 들어온 위치)에 놓여 클립 없이도 종이를 벗어나지 않는다.
 * @param box 메모지 기하
 * @param seed PRNG 시드 (`NOTE_GRAIN_SEED`)
 * @param count 점 개수
 */
export function noteGrainSpeckles(
  box: { x: number; y: number; w: number; h: number },
  seed: number,
  count: number
): Array<{ x: number; y: number; size: number; alpha: number }> {
  const rand = mulberry32(seed);
  const out: Array<{ x: number; y: number; size: number; alpha: number }> = [];
  for (let i = 0; i < count; i += 1) {
    const size = 1 + Math.floor(rand() * 2); // 1 또는 2
    const spanW = Math.max(0, box.w - size);
    const spanH = Math.max(0, box.h - size);
    const x = box.x + rand() * spanW;
    const y = box.y + rand() * spanH;
    const alpha = 0.05 + rand() * 0.03; // 0.05 ~ 0.08
    out.push({ x, y, size, alpha });
  }
  return out;
}

/**
 * 스타일·언어·텍스트에 맞는 PNG 좌표를 순수 함수로 계산한다.
 * @param text 정규화된 자막 (최대 12 grapheme)
 * @param language 현재 언어
 * @param style CaptionStyle 카탈로그 항목
 * @param measure 글자 폭을 재는 함수 (캔버스 measureText 또는 테스트용 가짜)
 */
export function captionStyleLayout(
  text: string,
  language: Language,
  style: CaptionStyle,
  measure: MeasureText
): CaptionLayout {
  const hasText = text.trim() !== "";
  const graphemes = captionLength(text);
  const spacingEm = style.letterSpacingEm ?? 0;
  const spacingAt = (px: number) => Math.round(px * spacingEm);

  // ── 1. 글자 크기 결정 (shrink-to-fit) ──────────────────────────────────────
  const basePx = captionFontPx(text) * 2; // PNG 는 배율 2배
  let fontPx = Math.round(basePx * style.sizeScale);

  // 가용 폭 계산 — center 는 양쪽 80px, left/right 는 6% 여백만
  const sideMarginPx = Math.round(CAPTION_PNG_W * SIDE_MARGIN_RATIO); // 86px
  const availableW =
    style.align === "center"
      ? CAPTION_PNG_W - CENTER_MARGIN_PX * 2 // 1280
      : CAPTION_PNG_W - sideMarginPx * 2; // 1268

  // 글자 외에 가용폭을 먹는 요소들 — 박스 패딩 · 양옆 ✦ · 끝 낙관 · 자간
  const hasBox =
    style.background === "tag" || style.background === "pill" || style.background === "note";
  const extraW = (px: number) =>
    (hasBox ? 2 * H_PAD_EM * px : 0) +
    (style.decoration === "ornaments" ? 2 * (ORNAMENT_GAP_EM + ORNAMENT_EM) * px : 0) +
    (style.decoration === "seal" ? (SEAL_GAP_EM + SEAL_EM) * px : 0) +
    spacingAt(px) * Math.max(0, graphemes - 1);

  // shrink-to-fit: 측정 후 폭이 넘치면 4px 씩 줄인다.
  // CAPTION_FIT_MIN_PX 는 축소 경로의 하한(sizeScale < 1 인 스타일은 그 아래로 자연히 내려갈 수 있다).
  const initialPx = fontPx;
  let font = captionStyleFont(style, language, fontPx);
  while (fontPx > CAPTION_FIT_MIN_PX) {
    font = captionStyleFont(style, language, fontPx);
    const measured = measure(font, text);
    if (measured + extraW(fontPx) <= availableW) break;
    fontPx -= 4;
  }
  // 실제로 줄어든 경우에만 하한 적용 — 자연 크기가 이미 작은 스타일(minimal 등)은 영향 없음
  if (fontPx < initialPx) {
    fontPx = Math.max(fontPx, Math.min(initialPx, CAPTION_FIT_MIN_PX));
  }
  font = captionStyleFont(style, language, fontPx);

  // ── 2. 글자 좌표 (기준점) ──────────────────────────────────────────────────
  const y = CAPTION_PNG_H * CAPTION_TEXT_Y_RATIO; // 297.6

  let x: number;
  if (style.align === "center") {
    x = CAPTION_PNG_W / 2; // 720
  } else if (style.align === "left") {
    x = sideMarginPx; // 86
  } else {
    x = CAPTION_PNG_W - sideMarginPx; // 1354
  }

  // ── 3. letterSpacing + 글자 advance ────────────────────────────────────────
  const letterSpacingPx = spacingAt(fontPx);
  const textAdvance =
    measure(font, text) + letterSpacingPx * Math.max(0, graphemes - 1);
  const textLeft =
    style.align === "center"
      ? x - textAdvance / 2
      : style.align === "left"
        ? x
        : x - textAdvance;

  // ── 4. shade ────────────────────────────────────────────────────────────────
  const shade = style.background === "shade";

  // ── 5. box (tag · pill · note) ────────────────────────────────────────────
  // 빈 텍스트(공백만)이면 박스·장식선을 그리지 않는다 — 빈 흰 박스가 보이는 문제 방지
  let box: CaptionLayout["box"];
  let note: CaptionLayout["note"];
  if (hasBox && hasText) {
    const hPad = H_PAD_EM * fontPx;
    // note(메모지)는 패딩이 두꺼워 PNG 아래로 삐져나올 수 있어 바닥 여백 기준으로 조인다
    const vPad =
      style.background === "note"
        ? Math.max(0, Math.min(
            NOTE_V_PAD_RATIO * fontPx,
            CAPTION_PNG_H - NOTE_BOTTOM_MARGIN_PX - (y + fontPx / 2)
          ))
        : V_PAD_RATIO * fontPx;
    const boxH = fontPx + vPad * 2;
    const radius =
      style.background === "tag" ? 6 : style.background === "note" ? NOTE_RADIUS : boxH / 2;

    // 박스가 PNG 밖으로 나가면 모서리·그림자가 잘려 "직각으로 자른 종이" 로 보인다.
    // 글자는 그대로 두고 **잘리는 쪽 패딩만** 줄여 박스를 안으로 들인다.
    // 기울임(rotateDeg)이 있으면 박스 상·하단이 회전으로 좌우로 더 밀려나므로 그만큼(lift)을 여백에 더한다.
    const lift = (Math.abs(Math.sin((style.rotateDeg * Math.PI) / 180)) * boxH) / 2;
    const edge = lift + BOX_EDGE_MARGIN_PX;
    const boxX = Math.max(edge, textLeft - hPad);
    const boxRight = Math.min(CAPTION_PNG_W - edge, textLeft + textAdvance + hPad);
    const boxW = boxRight - boxX;
    const boxY = y - fontPx / 2 - vPad;

    box = {
      x: boxX,
      y: boxY,
      w: boxW,
      h: boxH,
      radius,
      color: style.backgroundColor ?? (style.background === "note" ? "#FFF3A3" : "#FFFFFF"),
    };

    if (style.background === "note") {
      note = {
        shadow: NOTE_SHADOW,
        grain: {
          seed: NOTE_GRAIN_SEED,
          count: Math.floor((boxW * boxH) / NOTE_GRAIN_PX2_PER_SPECKLE),
          color: NOTE_GRAIN_COLOR,
        },
        tape: {
          cx: boxX + boxW / 2,
          cy: boxY,
          w: NOTE_TAPE.wEm * fontPx,
          h: NOTE_TAPE.hEm * fontPx,
          rotateDeg: NOTE_TAPE.rotateDeg,
          color: NOTE_TAPE.color,
        },
      };
    }
  }

  // ── 6. rules (poster) ────────────────────────────────────────────────────
  let rules: CaptionLayout["rules"];
  if (style.decoration === "rules" && hasText) {
    const gap = 0.18 * fontPx;
    const lineW = textAdvance + fontPx * 2; // 글자 폭 + 양쪽 fontPx
    rules = {
      x1: x - lineW / 2,
      x2: x + lineW / 2,
      y1: y - fontPx / 2 - gap, // 글자 위
      y2: y + fontPx / 2 + gap, // 글자 아래
      color: style.color,
    };
  }

  // ── 7. hairline (minimal) ─────────────────────────────────────────────────
  let hairline: CaptionLayout["hairline"];
  if (style.decoration === "hairline-top" && hasText) {
    const gap = 0.45 * fontPx;
    hairline = {
      x1: textLeft,
      x2: textLeft + textAdvance,
      y: y - fontPx / 2 - gap,
      color: style.color,
    };
  }

  // ── 8. fill (gilt 가로 · sea 세로 그라데이션) ──────────────────────────────
  // 끝점은 PNG 폭이 아니라 측정한 글자 범위 — 짧은 자막에서도 금빛이 글자 안에서 다 보인다
  let fill: CaptionLayout["fill"];
  if (style.fill && hasText) {
    fill =
      style.fill.direction === "horizontal"
        ? { colors: [...style.fill.colors], x0: textLeft, y0: y, x1: textLeft + textAdvance, y1: y }
        : {
            colors: [...style.fill.colors],
            x0: x,
            y0: y - fontPx / 2,
            x1: x,
            y1: y + fontPx / 2,
          };
  }

  // ── 9. ornaments (gilt 양옆 ✦) ────────────────────────────────────────────
  let ornaments: CaptionLayout["ornaments"];
  if (style.decoration === "ornaments" && hasText) {
    const px = ORNAMENT_EM * fontPx;
    const gap = ORNAMENT_GAP_EM * fontPx;
    const stack = style.font[language] ?? style.font.ko;
    ornaments = {
      glyph: "✦",
      font: `${style.font.weight} ${Math.round(px)}px ${stack}`,
      px,
      color: style.decorationColor ?? style.fill?.colors[0] ?? style.color,
      left: { x: textLeft - gap - px / 2, y },
      right: { x: textLeft + textAdvance + gap + px / 2, y },
    };
  }

  // ── 10. seal (brush 끝 낙관) ───────────────────────────────────────────────
  let seal: CaptionLayout["seal"];
  if (style.decoration === "seal" && hasText) {
    const size = SEAL_EM * fontPx;
    seal = {
      x: textLeft + textAdvance + SEAL_GAP_EM * fontPx,
      y: y + fontPx / 2 - size, // em 박스 하단에 맞춰 앉힌다
      size,
      radius: Math.round(size * 0.08),
      color: style.decorationColor ?? style.color,
      borderColor: VIDEO_PAPER_COLOR,
      borderWidth: Math.max(2, Math.round(size * 0.07)),
    };
  }

  // ── 11. effect ────────────────────────────────────────────────────────────
  return {
    font,
    fontPx,
    x,
    y,
    align: style.align,
    rotateDeg: style.rotateDeg,
    letterSpacingPx,
    color: style.color,
    shade,
    textLeft,
    textAdvance,
    box,
    rules,
    hairline,
    fill,
    ornaments,
    seal,
    note,
    effect: style.effect,
    glowColor: style.glowColor,
  };
}
